import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getHealthCheck } from "../lib/api";
import { StepContent } from "../components/StepContent";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  ChevronLeft, ChevronRight, ShieldCheck, Check, MessageSquare,
} from "lucide-react";

export default function HealthCheckDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState("");
  const [done, setDone] = useState(new Set());

  useEffect(() => {
    setLoading(true);
    getHealthCheck(id)
      .then((d) => { setData(d); setOpen(d.steps?.[0]?.id || ""); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  const toggleDone = (stepId) =>
    setDone((prev) => {
      const next = new Set(prev);
      next.has(stepId) ? next.delete(stepId) : next.add(stepId);
      return next;
    });

  if (loading) return <div className="p-8 text-zinc-500 font-mono text-sm">Loading health check…</div>;
  if (!data) return <div className="p-8 text-zinc-400">Health check not found. <button onClick={() => navigate("/health-checks")} className="text-emerald-400">Back</button></div>;

  const progress = Math.round((done.size / data.steps.length) * 100);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10 gap-3">
        <button onClick={() => navigate("/health-checks")} data-testid="hc-back-btn" className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Health Checks
        </button>
        <ChevronRight className="h-3 w-3 text-zinc-600" />
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold truncate">{data.title}</span>
      </header>

      <div className="p-5 sm:p-8 max-w-4xl">
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-400 font-semibold">
            <ShieldCheck className="h-4 w-4" /> Verification Checklist
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-white mt-3">{data.title}</h1>
          <p className="text-zinc-400 mt-3 leading-relaxed">{data.summary}</p>

          <div className="mt-5">
            <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-1.5">
              <span>{done.size} of {data.steps.length} checks verified</span>
              <span className="font-mono">{progress}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${progress}%` }} data-testid="hc-progress" />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">
            Checks ({data.steps.length})
          </div>
          <Accordion type="single" collapsible value={open} onValueChange={setOpen} className="space-y-3">
            {data.steps.map((step, idx) => (
              <AccordionItem
                key={step.id}
                value={step.id}
                data-testid={`hc-step-${step.id}`}
                className="border border-zinc-800 rounded-lg bg-zinc-900 overflow-hidden px-0"
              >
                <AccordionTrigger className="px-5 hover:no-underline hover:bg-zinc-800/40 [&[data-state=open]]:bg-zinc-800/30">
                  <div className="flex items-center gap-4 text-left">
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`hc-done-${step.id}`}
                      onClick={(e) => { e.stopPropagation(); toggleDone(step.id); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); toggleDone(step.id); } }}
                      className={`h-8 w-8 shrink-0 rounded-md font-mono text-sm font-bold flex items-center justify-center border cursor-pointer transition-colors ${done.has(step.id) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-zinc-950 border-zinc-700 text-zinc-400 hover:border-emerald-500/40"}`}
                      title={done.has(step.id) ? "Verified — click to undo" : "Mark verified"}
                    >
                      {done.has(step.id) ? <Check className="h-4 w-4" /> : idx + 1}
                    </span>
                    <div>
                      <div className="font-heading font-bold text-white text-base">{step.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 font-semibold">{step.layer}{done.has(step.id) ? " · verified" : ""}</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <StepContent step={step} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5 flex items-center justify-between gap-4">
          <div className="text-sm text-zinc-400">
            Something failing this check? Ask the assistant about <span className="text-white">{data.title}</span>.
          </div>
          <button
            onClick={() => navigate(`/assistant?context=${encodeURIComponent(data.title)}`)}
            data-testid="hc-ask-assistant-btn"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors shrink-0"
          >
            <MessageSquare className="h-4 w-4" /> Ask AI Assistant
          </button>
        </div>
      </div>
    </div>
  );
}
